import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { HelpKey } from '@zt/help';
import { Badge, Button, Field, Input, Section, Select, Spinner } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { ConsentStats, TrackingEvents, TrackingView } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';
import { ServerEventLog, ServerEventsSection } from './server-events';

/**
 * Ölçümleme (F5-07 çerez onayı, F5-08 vitrin etiketleri): mağaza başına etiket kimlikleri, vitrinde etkin olup
 * olmadığı, yasal metin etkisi (çerezler, yurt dışı aktarım) ve çerez onayı istatistikleri.
 */

type Settings = TrackingView['settings'];
type FieldKey = keyof Settings;

const FIELDS: Array<{ key: FieldKey; help: HelpKey; placeholder: string }> = [
  { key: 'gtmContainerId', help: 'tracking.gtm', placeholder: 'GTM-XXXXXXX' },
  { key: 'ga4MeasurementId', help: 'tracking.ga4', placeholder: 'G-XXXXXXXXXX' },
  { key: 'googleAdsId', help: 'tracking.google_ads', placeholder: 'AW-123456789' },
  { key: 'googleAdsPurchaseLabel', help: 'tracking.google_ads_label', placeholder: 'AbCdEf12345' },
  { key: 'metaPixelId', help: 'tracking.meta', placeholder: '123456789012345' },
  { key: 'tiktokPixelId', help: 'tracking.tiktok', placeholder: 'C0ABCDEFGHIJKLMN' },
];

export function TrackingPage() {
  const t = useT();
  const { stores, storeId, setStoreId } = useStoreChoice();
  const q = useQuery({
    queryKey: ['tracking', storeId],
    queryFn: () => api.get<TrackingView>(`/v1/admin/stores/${storeId}/tracking`),
    enabled: !!storeId,
  });
  return (
    <PageFrame helpKey="page.tracking">
      <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <StatusSection d={q.data} />
          <TagsSection key={storeId} storeId={storeId} d={q.data} />
          <CrossDomainSection storeId={storeId} d={q.data} />
          {q.data.providers.length > 0 && <LegalSection d={q.data} />}
          <EventsSection key={`ev-${storeId}`} storeId={storeId} />
          <ServerEventsSection storeId={storeId} />
          <ServerEventLog storeId={storeId} />
          <StatsSection storeId={storeId} />
        </>
      )}
    </PageFrame>
  );
}

function StatusSection({ d }: { d: TrackingView }) {
  const t = useT();
  return (
    <Section
      title={t('admin.tracking.statusTitle')}
      description={t('admin.tracking.statusLead')}
      action={<HelpTip k="tracking.status" />}
    >
      <p className="flex flex-wrap items-center gap-2">
        <Badge tone={d.active ? 'moss' : d.inactiveReason === 'no_tags' ? 'slate' : 'amber'}>
          {d.active ? t('admin.tracking.active') : t('admin.tracking.inactive')}
        </Badge>
        <span className="text-sm text-ink-2">
          {d.active
            ? t('admin.tracking.activeLead', {
                list: d.providers.map((p) => t(`admin.tracking.providers.${p}`)).join(', '),
              })
            : t(`admin.tracking.reasons.${d.inactiveReason ?? 'no_tags'}`)}
        </span>
        {d.inactiveReason === 'cookie_policy_missing' && (
          <Link to="/yasal-metinler" className="text-sm text-pine hover:underline">
            {t('admin.tracking.toLegal')}
          </Link>
        )}
      </p>
    </Section>
  );
}

function TagsSection({ storeId, d }: { storeId: string; d: TrackingView }) {
  const t = useT();
  const { can } = usePermissions();
  const canWrite = can('tracking.manage');
  const [draft, setDraft] = useState<Record<FieldKey, string> | null>(null);
  const f: Record<FieldKey, string> =
    draft ??
    (Object.fromEntries(FIELDS.map(({ key }) => [key, d.settings[key] ?? ''])) as Record<FieldKey, string>);
  const save = useApiMutation(
    () =>
      api.put(
        `/v1/admin/stores/${storeId}/tracking`,
        Object.fromEntries(FIELDS.map(({ key }) => [key, f[key].trim() || null])),
      ),
    {
      invalidate: [['tracking', storeId]],
      success: t('admin.tracking.saved'),
      onSuccess: () => setDraft(null),
      silentFieldErrors: true,
    },
  );
  return (
    <Section title={t('admin.tracking.tagsTitle')} description={t('admin.tracking.tagsLead')}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(undefined);
        }}
      >
        <fieldset disabled={!canWrite} className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map(({ key, help, placeholder }) => (
            <Field
              key={key}
              label={t(`admin.tracking.fields.${key}`)}
              error={fieldError(save.error, key)}
              help={<HelpTip k={help} />}
            >
              {(p) => (
                <Input
                  {...p}
                  value={f[key]}
                  placeholder={placeholder}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => setDraft({ ...f, [key]: e.target.value })}
                />
              )}
            </Field>
          ))}
        </fieldset>
        <p className="text-sm text-ink-3">{t('admin.tracking.consentNote')}</p>
        {canWrite && (
          <span className="flex items-center gap-2">
            <Button type="submit" disabled={save.isPending || !draft}>
              {t('admin.tracking.save')}
            </Button>
            <HelpTip k="tracking.save" />
          </span>
        )}
      </form>
    </Section>
  );
}

/**
 * Alanlar arası izleme (F7-21, ADR-125): mağazanın birden çok vitrin alan adı (ör. ikinci alan adı veya aynı GA4
 * kimliğini kullanan başka mağaza) varsa vitrin GA4 bağlayıcısını ve kaynak bilgisinin taşınmasını kendiliğinden
 * açar. GA4 yönetimindeki alan adı listesi işletmenin elle yaptığı adımdır (rehberdeki adımlar).
 */
function CrossDomainSection({ storeId, d }: { storeId: string; d: TrackingView }) {
  const t = useT();
  const x = d.crossDomain;
  return (
    <Section
      title={t('admin.tracking.crossDomain.title')}
      description={t('admin.tracking.crossDomain.lead')}
      action={<HelpTip k="tracking.cross_domain" />}
    >
      <p className="flex flex-wrap items-center gap-2">
        <Badge tone={x.active ? 'moss' : 'slate'}>
          {x.active ? t('admin.tracking.crossDomain.active') : t('admin.tracking.crossDomain.inactive')}
        </Badge>
        <span className="text-sm text-ink-2">
          {x.active
            ? t('admin.tracking.crossDomain.activeLead', { count: x.domains.length })
            : t(`admin.tracking.crossDomain.reasons.${x.reason ?? 'single_domain'}`)}
        </span>
        {x.reason === 'single_domain' && (
          <Link to="/magazalar/$id" params={{ id: storeId }} className="text-sm text-pine hover:underline">
            {t('admin.tracking.crossDomain.toDomains')}
          </Link>
        )}
      </p>
      {x.active && (
        <>
          <ul
            className="mt-3 flex flex-wrap gap-2 text-sm"
            aria-label={t('admin.tracking.crossDomain.domains')}
          >
            {x.domains.map((h) => (
              <li key={h} className="rounded border border-line bg-surface-2 px-2 py-0.5 font-medium">
                {h}
              </li>
            ))}
          </ul>
          <h3 className="mt-4 text-sm font-medium">{t('admin.tracking.crossDomain.stepsTitle')}</h3>
          <ol className="mt-1 max-w-[72ch] list-decimal space-y-1 ps-5 text-sm text-ink-2">
            <li>{t('admin.tracking.crossDomain.stepAuto')}</li>
            <li>{t('admin.tracking.crossDomain.stepGa4')}</li>
            <li>{t('admin.tracking.crossDomain.stepReferrals')}</li>
            <li>{t('admin.tracking.crossDomain.stepConsent')}</li>
          </ol>
        </>
      )}
    </Section>
  );
}

function LegalSection({ d }: { d: TrackingView }) {
  const t = useT();
  const { locale } = useI18n();
  const countries = new Intl.DisplayNames([locale], { type: 'region' });
  const yes = (ok: boolean) => (
    <Badge tone={ok ? 'moss' : 'amber'}>
      {ok ? t('admin.tracking.legal.found') : t('admin.tracking.legal.missing')}
    </Badge>
  );
  const missing =
    d.legal.cookies.some((c) => !c.inPolicy) ||
    d.legal.transfers.some((x) => !x.inNotice || !x.inCookiePolicy);
  return (
    <Section
      title={t('admin.tracking.legal.title')}
      description={t('admin.tracking.legal.lead')}
      action={<HelpTip k="tracking.legal" />}
    >
      <p className="mb-3 text-sm">
        {d.legal.cookiePolicy.published
          ? t('admin.tracking.legal.policyVersion', { version: d.legal.cookiePolicy.version ?? 0 })
          : t('admin.tracking.legal.policyMissing')}{' '}
        {missing && <strong>{t('admin.tracking.legal.actionNeeded')}</strong>}
      </p>
      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full text-sm" aria-label={t('admin.tracking.legal.cookiesTitle')}>
          <caption className="px-3 py-2 text-start font-medium">
            {t('admin.tracking.legal.cookiesTitle')}
          </caption>
          <thead className="bg-surface-2 text-ink-2">
            <tr>
              <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.legal.colCookie')}</th>
              <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.legal.colService')}</th>
              <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.legal.colDuration')}</th>
              <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.legal.colInPolicy')}</th>
            </tr>
          </thead>
          <tbody>
            {d.legal.cookies.map((c) => (
              <tr key={`${c.provider}-${c.name}`} className="border-t border-line">
                <td className="px-3 py-2 font-mono text-[0.8125rem]">{c.name}</td>
                <td className="px-3 py-2">{t(`admin.tracking.providers.${c.provider}`)}</td>
                <td className="px-3 py-2">{t('admin.tracking.legal.days', { count: c.days })}</td>
                <td className="px-3 py-2">{yes(c.inPolicy)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 overflow-x-auto rounded-md border border-line">
        <table className="w-full text-sm" aria-label={t('admin.tracking.legal.transfersTitle')}>
          <caption className="px-3 py-2 text-start font-medium">
            {t('admin.tracking.legal.transfersTitle')}
          </caption>
          <thead className="bg-surface-2 text-ink-2">
            <tr>
              <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.legal.colService')}</th>
              <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.legal.colRecipient')}</th>
              <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.legal.colCountries')}</th>
              <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.legal.colInNotice')}</th>
              <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.legal.colInPolicy')}</th>
            </tr>
          </thead>
          <tbody>
            {d.legal.transfers.map((x) => (
              <tr key={x.provider} className="border-t border-line">
                <td className="px-3 py-2">{t(`admin.tracking.providers.${x.provider}`)}</td>
                <td className="px-3 py-2">{x.recipient}</td>
                <td className="px-3 py-2">{x.countries.map((c) => countries.of(c) ?? c).join(', ')}</td>
                <td className="px-3 py-2">{yes(x.inNotice)}</td>
                <td className="px-3 py-2">{yes(x.inCookiePolicy)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-sm text-ink-3">{t('admin.tracking.legal.note')}</p>
    </Section>
  );
}

function StatsSection({ storeId }: { storeId: string }) {
  const t = useT();
  const q = useQuery({
    queryKey: ['consent-stats', storeId],
    queryFn: () => api.get<ConsentStats>(`/v1/admin/consent-stats?storeId=${storeId}&days=30`),
  });
  const s = q.data;
  const pct = (n: number) => (s && s.visitors ? `%${Math.round((n / s.visitors) * 100)}` : '—');
  return (
    <Section
      title={t('admin.tracking.stats.title')}
      description={t('admin.tracking.stats.lead')}
      action={<HelpTip k="tracking.stats" />}
    >
      {!s ? (
        <Spinner label={t('common.loading')} />
      ) : s.visitors === 0 ? (
        <p className="text-sm text-ink-2">{t('admin.tracking.stats.none')}</p>
      ) : (
        <dl className="grid gap-3 sm:grid-cols-3">
          {(
            [
              ['visitors', String(s.visitors)],
              ['acceptAll', `${s.acceptAll} (${pct(s.acceptAll)})`],
              ['rejectAll', `${s.rejectAll} (${pct(s.rejectAll)})`],
              ['custom', `${s.custom} (${pct(s.custom)})`],
              ['analytics', `${s.analytics} (${pct(s.analytics)})`],
              ['marketing', `${s.marketing} (${pct(s.marketing)})`],
            ] as const
          ).map(([k, v]) => (
            <div key={k} className="rounded-md border border-line px-3 py-2">
              <dt className="text-sm text-ink-2">{t(`admin.tracking.stats.${k}`)}</dt>
              <dd className="text-lg font-medium">{v}</dd>
            </div>
          ))}
        </dl>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------- Olay yöneticisi (F6-22)

const STANDARD = [
  'view_item_list',
  'select_item',
  'view_item',
  'add_to_cart',
  'remove_from_cart',
  'view_cart',
  'begin_checkout',
  'add_shipping_info',
  'add_payment_info',
  'purchase',
  'search',
  'sign_up',
  'login',
  'generate_lead',
  // Evcil Dostumun İhtiyacı (F7-39): soru akışı (yalnızca GA4/GTM; cevaplar gönderilmez).
  'quiz_start',
  'quiz_step',
  'quiz_complete',
] as const;
const DESTS = ['gtm', 'ga4', 'meta', 'tiktok'] as const;
type Dest = (typeof DESTS)[number];
/** Meta ve TikTok'un standart karşılığı olan olaylar (vitrin yalnızca bunları gönderir). */
const PIXEL_EVENTS = new Set([
  'view_item',
  'add_to_cart',
  'begin_checkout',
  'add_payment_info',
  'purchase',
  'search',
  'sign_up',
  'generate_lead',
]);
const TRIGGERS = ['page_view', 'click_phone', 'click_email', 'click_whatsapp', 'click_outbound'] as const;
type Custom = TrackingEvents['custom'][number];

function EventsSection({ storeId }: { storeId: string }) {
  const t = useT();
  const { can } = usePermissions();
  const canWrite = can('tracking.manage');
  const q = useQuery({
    queryKey: ['tracking-events', storeId],
    queryFn: () => api.get<TrackingEvents>(`/v1/admin/stores/${storeId}/tracking/events`),
  });
  if (!q.data) return <Spinner label={t('common.loading')} />;
  return <EventsForm key={JSON.stringify(q.data)} storeId={storeId} saved={q.data} canWrite={canWrite} />;
}

function EventsForm({
  storeId,
  saved,
  canWrite,
}: {
  storeId: string;
  saved: TrackingEvents;
  canWrite: boolean;
}) {
  const t = useT();
  const [disabled, setDisabled] = useState<Record<Dest, string[]>>(saved.disabled);
  const [custom, setCustom] = useState<Custom[]>(saved.custom);
  const [draft, setDraft] = useState<{
    name: string;
    trigger: Custom['trigger'];
    path: string;
    dests: Dest[];
  }>({
    name: '',
    trigger: 'page_view',
    path: '',
    dests: ['ga4'],
  });
  const dirty = JSON.stringify({ disabled, custom }) !== JSON.stringify(saved);
  const save = useApiMutation(
    () => api.put<TrackingEvents>(`/v1/admin/stores/${storeId}/tracking/events`, { disabled, custom }),
    {
      invalidate: [['tracking-events', storeId]],
      success: t('admin.trackingEvents.saved'),
      silentFieldErrors: true,
    },
  );
  const toggle = (d: Dest, ev: string, on: boolean) =>
    setDisabled((x) => ({ ...x, [d]: on ? x[d].filter((e) => e !== ev) : [...x[d], ev] }));
  const add = () => {
    setCustom((c) => [
      ...c,
      {
        name: draft.name.trim(),
        trigger: draft.trigger,
        path: draft.trigger === 'page_view' ? draft.path.trim() || null : null,
        destinations: draft.dests,
      },
    ]);
    setDraft({ name: '', trigger: 'page_view', path: '', dests: ['ga4'] });
  };
  const errors = save.error?.errors ?? [];
  return (
    <Section
      title={t('admin.trackingEvents.title')}
      description={t('admin.trackingEvents.lead')}
      action={<HelpTip k="tracking.events" />}
    >
      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full text-sm" aria-label={t('admin.trackingEvents.standardTitle')}>
          <caption className="flex items-center gap-1 px-3 pt-2 text-start font-medium">
            {t('admin.trackingEvents.standardTitle')} <HelpTip k="tracking.events_standard" />
          </caption>
          <thead className="text-ink-2">
            <tr>
              <th className="px-3 py-2 text-start font-medium">{t('admin.trackingEvents.event')}</th>
              {DESTS.map((d) => (
                <th key={d} className="px-3 py-2 text-center font-medium">
                  {t(`admin.tracking.providers.${d}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {STANDARD.map((ev) => (
              <tr key={ev} className="border-t border-line">
                <td className="px-3 py-2">
                  {t(`admin.trackingEvents.events.${ev}`)}
                  <code className="ms-2 text-xs text-ink-3">{ev}</code>
                </td>
                {DESTS.map((d) => (
                  <td key={d} className="px-3 py-2 text-center">
                    {(d === 'meta' || d === 'tiktok') && !PIXEL_EVENTS.has(ev) ? (
                      <span className="text-ink-3" aria-label={t('admin.trackingEvents.notApplicable')}>
                        —
                      </span>
                    ) : (
                      <input
                        type="checkbox"
                        className="accent-[var(--zt-pine)]"
                        checked={!disabled[d].includes(ev)}
                        disabled={!canWrite}
                        aria-label={t('admin.trackingEvents.sendTo', {
                          event: t(`admin.trackingEvents.events.${ev}`),
                          provider: t(`admin.tracking.providers.${d}`),
                        })}
                        onChange={(e) => toggle(d, ev, e.target.checked)}
                      />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="mt-6 flex items-center gap-1 font-medium">
        {t('admin.trackingEvents.customTitle')} <HelpTip k="tracking.custom_events" />
      </h3>
      <p className="mt-1 text-sm text-ink-2">{t('admin.trackingEvents.customLead')}</p>
      {custom.length === 0 ? (
        <p className="mt-3 text-sm text-ink-3">{t('admin.trackingEvents.noCustom')}</p>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-md border border-line">
          <table className="w-full text-sm" aria-label={t('admin.trackingEvents.customTitle')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.trackingEvents.name')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.trackingEvents.trigger')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.trackingEvents.destinations')}</th>
                {canWrite && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {custom.map((c, i) => {
                const err = errors.find((e) => e.field.startsWith(`custom.${i}.`));
                return (
                  <tr key={`${c.name}-${i}`} className="border-t border-line align-top">
                    <td className="px-3 py-2">
                      <code>{c.name}</code>
                      {err && (
                        <span role="alert" className="mt-1 block text-xs text-rose">
                          {err.message}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {t(`admin.trackingEvents.triggers.${c.trigger}`)}
                      {c.path && <code className="ms-2 text-xs text-ink-3">{c.path}</code>}
                    </td>
                    <td className="px-3 py-2">
                      {c.destinations.map((d) => t(`admin.tracking.providers.${d}`)).join(', ')}
                    </td>
                    {canWrite && (
                      <td className="px-3 py-2 text-end">
                        <span className="inline-flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setCustom((x) => x.filter((_, j) => j !== i))}
                          >
                            {t('admin.trackingEvents.remove', { name: c.name })}
                          </Button>
                          <HelpTip k="tracking.custom_event_remove" />
                        </span>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {canWrite && custom.length < 20 && (
        <fieldset className="mt-4 rounded-md border border-line p-3">
          <legend className="px-1 text-sm font-medium">{t('admin.trackingEvents.addTitle')}</legend>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field
              label={t('admin.trackingEvents.name')}
              description={t('admin.trackingEvents.nameHint')}
              help={<HelpTip k="tracking.custom_event_name" />}
            >
              {(p) => (
                <Input
                  {...p}
                  value={draft.name}
                  placeholder="kampanya_gorundu"
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('admin.trackingEvents.trigger')}
              help={<HelpTip k="tracking.custom_event_trigger" />}
            >
              {(p) => (
                <Select
                  {...p}
                  value={draft.trigger}
                  onChange={(e) => setDraft({ ...draft, trigger: e.target.value as Custom['trigger'] })}
                >
                  {TRIGGERS.map((tr) => (
                    <option key={tr} value={tr}>
                      {t(`admin.trackingEvents.triggers.${tr}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {draft.trigger === 'page_view' && (
              <Field
                label={t('admin.trackingEvents.path')}
                description={t('admin.trackingEvents.pathHint')}
                help={<HelpTip k="tracking.custom_event_path" />}
              >
                {(p) => (
                  <Input
                    {...p}
                    value={draft.path}
                    placeholder="/kampanya/*"
                    autoComplete="off"
                    spellCheck={false}
                    onChange={(e) => setDraft({ ...draft, path: e.target.value })}
                  />
                )}
              </Field>
            )}
          </div>
          <div
            className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm"
            role="group"
            aria-label={t('admin.trackingEvents.destinations')}
          >
            <span className="inline-flex items-center gap-1 text-ink-2">
              {t('admin.trackingEvents.destinations')} <HelpTip k="tracking.custom_event_destinations" />
            </span>
            {DESTS.map((d) => (
              <label key={d} className="inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  className="accent-[var(--zt-pine)]"
                  checked={draft.dests.includes(d)}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      dests: e.target.checked ? [...draft.dests, d] : draft.dests.filter((x) => x !== d),
                    })
                  }
                />
                {t(`admin.tracking.providers.${d}`)}
              </label>
            ))}
          </div>
          <span className="mt-3 inline-flex items-center gap-1">
            <Button size="sm" disabled={!draft.name.trim()} onClick={add}>
              {t('admin.trackingEvents.add')}
            </Button>
            <HelpTip k="tracking.custom_event_add" />
          </span>
        </fieldset>
      )}

      <p className="mt-4 text-sm text-ink-3">{t('admin.trackingEvents.consentNote')}</p>
      {canWrite && (
        <span className="mt-3 flex items-center gap-2">
          <Button
            variant="primary"
            disabled={!dirty || save.isPending}
            loading={save.isPending}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.trackingEvents.save')}
          </Button>
          <HelpTip k="tracking.events_save" />
        </span>
      )}
      {save.error && !save.error.errors.length && (
        <p role="alert" className="mt-2 text-sm text-rose">
          {save.error.title}
        </p>
      )}
    </Section>
  );
}
